import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../../services/auth.service';
import { ToastService } from '../../services/toast.service';
import { LanguageService } from '../../services/language.service';
import { LanguageSelectorComponent } from '../../components/shared/language-selector/language-selector';
import { UserNotFoundModalComponent } from '../../components/shared/user-not-found-modal/user-not-found-modal';
import { ForgotPasswordModalComponent } from '../../components/shared/forgot-password-modal/forgot-password-modal';
import { RecaptchaVerifier, ConfirmationResult } from '@angular/fire/auth';
import { combineLatest, Subscription } from 'rxjs';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, LanguageSelectorComponent, UserNotFoundModalComponent, ForgotPasswordModalComponent],
  templateUrl: './login.html',
  styleUrl: './login.scss'
})
export class Login implements OnInit, OnDestroy {
  authForm: FormGroup;
  otpForm: FormGroup;
  isLoading = false;
  errorMessage = '';
  successMessage = '';
  showPassword = false;
  showUserNotFoundModal = false;
  showForgotPasswordModal = false;

  // Demo mode toggle - set to false when Firebase is properly configured
  isDemoMode = false; // Change to false when Firebase phone auth is enabled

  // Phone auth state
  showOtpInput = false;
  otpSent = false;
  recaptchaVerifier!: RecaptchaVerifier;
  confirmationResult!: ConfirmationResult;
  countdown = 0;
  countdownInterval: any;

  // Language functionality - now handled by LanguageService
  private languageSubscription?: Subscription;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private toastService: ToastService,
    private languageService: LanguageService
  ) {
    this.authForm = this.createForm();
    this.otpForm = this.createOtpForm();
  }

  ngOnInit(): void {
    // Capture returnUrl (if any) from query params and store for post-login redirect
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    if (returnUrl) {
      this.authService.setRedirectUrl(returnUrl);
    }
    
    // Subscribe to both language and translations changes
    // This ensures UI updates when translations load after page refresh
    this.languageSubscription = combineLatest([
      this.languageService.currentLanguage$,
      this.languageService.translations$
    ]).subscribe(() => {
      this.cdr.markForCheck();
    });
    
    // Initialize reCAPTCHA if not in demo mode
    // if (!this.isDemoMode) {
    //   // Wait for DOM to be ready, then initialize reCAPTCHA
    //   setTimeout(() => {
    //     this.initializeRecaptcha();
    //   }, 100);
    // }
  }

  ngOnDestroy(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    if (this.languageSubscription) {
      this.languageSubscription.unsubscribe();
    }
  }

  createForm(): FormGroup {
    // Mobile number and password form
    return this.fb.group({
      mobile: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
      password: ['', [Validators.required, Validators.minLength(6)]]
    });
  }

  createOtpForm(): FormGroup {
    return this.fb.group({
      otp: ['', [Validators.required, Validators.pattern(/^[0-9]{6}$/)]]
    });
  }

  toggleMode(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    console.log('toggleMode clicked - navigating to register');
    // Redirect to register page
    try {
      this.router.navigate(['/register']);
      console.log('Navigation to register initiated');
    } catch (error) {
      console.error('Navigation error:', error);
    }
  }

  onSubmit(): void {
    if (this.authForm.valid) {
      this.performLogin();
    }else{
        this.toastService.error('Mobile no or password is invalid');
    }
  }

  private performLogin(): void {
    // Direct login with mobile and password
    this.loginWithMobilePassword();
  }

  // Initialize reCAPTCHA verifier
  private initializeRecaptcha(): void {
    try {
      // Check if reCAPTCHA container exists
      const container = document.getElementById('recaptcha-container');
      if (!container) {
        console.error('reCAPTCHA container not found');
        this.toastService.error('reCAPTCHA container not found. Please refresh the page.');
        return;
      }

      // If container already has content (reCAPTCHA is already rendered), don't reinitialize
      if (container.children.length > 0) {
        console.log('reCAPTCHA already initialized, skipping...');
        return;
      }

      // Clear any existing verifier
      if (this.recaptchaVerifier) {
        try {
          this.recaptchaVerifier.clear();
        } catch (clearError) {
          console.warn('Error clearing existing reCAPTCHA:', clearError);
        }
      }

      // Create new verifier
      this.recaptchaVerifier = this.authService.createRecaptchaVerifier('recaptcha-container');
      console.log('reCAPTCHA verifier initialized successfully');
    } catch (error) {
      console.error('Error initializing reCAPTCHA:', error);
      this.toastService.error('Failed to initialize verification system. Please refresh the page.');
    }
  }

  // Login with mobile and password
  private loginWithMobilePassword(): void {
    this.isLoading = true;
    this.errorMessage = '';
    
    const mobile = this.authForm.value.mobile;
    const password = this.authForm.value.password;
    
    if (this.isDemoMode) {
      // Demo mode: Simulate login
      console.log('Demo mode: Simulating login for mobile:', mobile);
      
      setTimeout(() => {
        this.isLoading = false;
        if (mobile === '1234567890' && password === 'demo123') {
          this.toastService.success(this.t('loginSuccess') || 'Login successful!');
          setTimeout(() => {
            const redirectUrl = this.authService.getRedirectUrl();
            if (redirectUrl && redirectUrl !== '/' && redirectUrl !== '/home') {
              this.router.navigateByUrl(redirectUrl);
            } else {
              this.router.navigate(['/home']);
            }
          }, 800);
        } else {
          // In demo mode, show modal for unregistered users
          this.showUserNotFoundModal = true;
        }
      }, 1500);
      return;
    }
    
    // Login with mobile and password through auth service
    this.authService.loginWithMobilePassword({ mobile, password }).subscribe({
      next: (result) => {
        this.isLoading = false;
        if (result.success) {
          this.toastService.success(this.t('loginSuccess') || 'Login successful!');
          setTimeout(() => {
            // Get redirect URL or default to home
            const redirectUrl = this.authService.getRedirectUrl();
            if (redirectUrl && redirectUrl !== '/' && redirectUrl !== '/home') {
              this.router.navigateByUrl(redirectUrl);
            } else {
              this.router.navigate(['/home']);
            }
          }, 800);
        } else {
          // Check if it's a user not found error
          if (result.message.includes('No account found') || result.message.includes('mobile number')) {
            this.showUserNotFoundModal = true;
            this.errorMessage = '';
          } else {
            this.toastService.error(result.message);
          }
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.toastService.error('Login failed. Please try again.');
        console.error('Login error:', error);
      }
    });
  }



  onContainerClick(event: Event): void {
    const target = event.target as HTMLElement;
    console.log('Container clicked, target:', target.className, target.tagName);

    // Don't interfere with create account link clicks
    if (target.closest('.create-account-link')) {
      console.log('Create account link area clicked - not interfering');
      return;
    }
  }

  // Translation method - now uses LanguageService
  t(key: string, params?: { [key: string]: string | number }): string {
    return this.languageService.t(key, params);
  }

  // Getter methods for form controls
  getMobileControl() {
    return this.authForm.get('mobile');
  }

  getPasswordControl() {
    return this.authForm.get('password');
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  closeUserNotFoundModal(): void {
    this.showUserNotFoundModal = false;
  }

  openForgotPasswordModal(): void {
    this.showForgotPasswordModal = true;
  }

  closeForgotPasswordModal(): void {
    this.showForgotPasswordModal = false;
  }

  // Google Sign-in method with Firebase
  signInWithGoogle(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.authService.loginWithGoogle().subscribe({
      next: (result) => {
        this.isLoading = false;
        if (result.success) {
          this.toastService.success(this.t('googleLoginSuccess') || 'Google login successful!');
          setTimeout(() => {
            this.router.navigate(['/home']);
          }, 1500);
        } else {
          this.toastService.error(result.message);
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.toastService.error('Google login failed. Please try again.');
        console.error('Google login error:', error);
      }
    });
  }
}
